// The case Kanban: the ordered on-board lanes rendered as a horizontally-scrollable row, plus a
// collapsible off-board tray (on_hold/remake/cancelled). Pure presentation — the routed CasesPage
// owns loading, live refresh, and the mutations. Cards surface a per-card "Advance", except at the
// approval gate where an aligner case shows "Approve setup" (the doctor sign-off) instead.
import {
  BOARD_COLUMNS,
  CASE_PRIORITY_LABELS,
  CASE_TYPE_LABELS,
  formatDate,
  nextStage,
  type Case,
  type CaseBoard as CaseBoardData,
  type CasePriority,
  type BoardColumn,
} from '@alignova/shared';
import { t } from '../../i18n/index.ts';
import { Button, Card, Pill } from '../../ui/primitives.tsx';

// Elevated priorities read as an alarm (bad); normal is quiet (neutral).
function priorityTone(priority: CasePriority): 'neutral' | 'bad' {
  return priority === 'urgent' || priority === 'same_day' ? 'bad' : 'neutral';
}

function CaseCard({
  c,
  onAdvance,
  onApprove,
  onOpen,
}: {
  c: Case;
  onAdvance: (c: Case) => void;
  onApprove?: (c: Case) => void;
  onOpen?: (c: Case) => void;
}) {
  const onBoard = BOARD_COLUMNS.includes(c.status);
  const atGate = c.status === 'approval';
  const canAdvance = onBoard && !atGate && nextStage(c.caseType, c.status) !== null;
  return (
    <div
      onClick={() => onOpen?.(c)}
      className={onOpen ? 'cursor-pointer' : undefined}
      role={onOpen ? 'button' : undefined}
    >
      <Card className="p-3 hover:border-primary">
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono text-xs text-accent">{c.caseNumber}</span>
          <Pill tone={priorityTone(c.priority)}>{CASE_PRIORITY_LABELS[c.priority]}</Pill>
        </div>
        <p className="mt-1 font-semibold text-ink">{c.clientName}</p>
        <p className="text-sm text-muted">{c.service}</p>
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted">
            {CASE_TYPE_LABELS[c.caseType]}
            {c.relationType ? ` · ${c.relationType}` : ''}
          </span>
          <span className="text-xs text-muted">{formatDate(c.dueDate)}</span>
        </div>
        {(atGate && onApprove) || canAdvance ? (
          <div className="mt-2 flex justify-end">
            {atGate && onApprove ? (
              <Button
                className="px-2 py-1 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove(c);
                }}
              >
                {t('cases.approve')}
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onAdvance(c);
                }}
              >
                {t('cases.advance')}
              </Button>
            )}
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function Lane({
  column,
  onAdvance,
  onApprove,
  onOpen,
}: {
  column: BoardColumn;
  onAdvance: (c: Case) => void;
  onApprove?: (c: Case) => void;
  onOpen?: (c: Case) => void;
}) {
  return (
    <section className="flex w-72 shrink-0 flex-col rounded-lg border border-line bg-surface-2">
      <header className="flex items-center justify-between border-b border-line px-3 py-2">
        <span className="text-sm font-medium text-ink">{column.label}</span>
        <span className="font-mono text-xs text-muted">{column.cases.length}</span>
      </header>
      <div className="flex flex-col gap-2 p-2">
        {column.cases.length === 0 ? (
          <p className="px-1 py-4 text-center text-xs text-muted">{t('cases.laneEmpty')}</p>
        ) : (
          column.cases.map((c) => (
            <CaseCard key={c.id} c={c} onAdvance={onAdvance} onApprove={onApprove} onOpen={onOpen} />
          ))
        )}
      </div>
    </section>
  );
}

export function CaseBoard({
  board,
  onAdvance,
  onApprove,
  onOpen,
}: {
  board: CaseBoardData;
  onAdvance: (c: Case) => void;
  onApprove?: (c: Case) => void;
  onOpen?: (c: Case) => void;
}) {
  const offBoardCount = board.offBoard.reduce((sum, col) => sum + col.cases.length, 0);
  return (
    <div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {board.columns.map((column) => (
          <Lane key={column.status} column={column} onAdvance={onAdvance} onApprove={onApprove} onOpen={onOpen} />
        ))}
      </div>

      <details className="mt-4 rounded-lg border border-line bg-surface">
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink">
          {t('cases.offBoard')}
          <span className="ml-2 font-mono text-xs text-muted">{offBoardCount}</span>
        </summary>
        <div className="flex gap-3 overflow-x-auto border-t border-line p-3">
          {board.offBoard.map((column) => (
            <Lane key={column.status} column={column} onAdvance={onAdvance} onApprove={onApprove} onOpen={onOpen} />
          ))}
        </div>
      </details>
    </div>
  );
}
